import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import axios from "axios";
import SEO, { blogPostSchema, breadcrumbSchema } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function renderMarkdownContent(content) {
  if (!content) return null;
  const lines = content.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("### ")) {
      elements.push(<h3 key={i}>{line.substring(4)}</h3>);
    } else if (line.startsWith("## ")) {
      elements.push(<h2 key={i}>{line.substring(3)}</h2>);
    } else if (line.startsWith("- ")) {
      const listItems = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        const text = lines[i].substring(2);
        listItems.push(
          <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(text) }} />
        );
        i++;
      }
      elements.push(<ul key={`ul-${i}`}>{listItems}</ul>);
      continue;
    } else if (/^\d+\.\s/.test(line)) {
      const listItems = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const text = lines[i].replace(/^\d+\.\s/, "");
        listItems.push(
          <li key={i} dangerouslySetInnerHTML={{ __html: formatInline(text) }} />
        );
        i++;
      }
      elements.push(<ol key={`ol-${i}`}>{listItems}</ol>);
      continue;
    } else if (line.trim()) {
      elements.push(
        <p key={i} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
      );
    }
    i++;
  }
  return elements;
}

function formatInline(text) {
  return text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        setPost(res.data);
      } catch {
        console.error("Failed to fetch post");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="h-8 w-48 bg-[#F5F2EB] animate-pulse mb-8" />
          <div className="h-12 w-full bg-[#F5F2EB] animate-pulse mb-4" />
          <div className="h-6 w-96 bg-[#F5F2EB] animate-pulse" />
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen bg-[#FDFBF7] pt-28 pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-3xl font-medium text-[#2D2D2D] mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-[#D4AF37] underline underline-offset-4">
            Back to Journal
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="min-h-screen bg-[#FDFBF7] pt-28 pb-24">
      <SEO
        title={post.title}
        path={`/blog/${post.slug}`}
        description={post.excerpt || post.title}
        type="article"
        jsonLd={[
          blogPostSchema(post),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Journal", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]}
      />
      <article className="max-w-3xl mx-auto px-6 md:px-12">
        <Link
          to="/blog"
          data-testid="blog-post-back-link"
          className="inline-flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#D4AF37] transition-colors mb-12"
        >
          <ArrowLeft size={14} /> Back to Journal
        </Link>

        <header className="mb-16">
          <p className="text-sm text-[#6B6B6B] mb-4">
            {new Date(post.created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
            {" "}  &middot;  {post.author}
          </p>
          <h1
            data-testid="blog-post-title"
            className="text-4xl md:text-5xl font-medium tracking-tight leading-[1.15] text-[#2D2D2D] mb-6"
          >
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="text-xl font-light leading-relaxed text-[#6B6B6B] font-['Cormorant_Garamond'] italic">
              {post.excerpt}
            </p>
          )}
        </header>

        <div className="blog-content" data-testid="blog-post-content">
          {renderMarkdownContent(post.content)}
        </div>

        <div className="border-t border-[rgba(45,45,45,0.08)] mt-16 pt-8">
          <Link
            to="/apply"
            className="btn-gold inline-flex items-center gap-2"
          >
            Apply for a Consultation
          </Link>
        </div>
      </article>
    </main>
  );
}
